import React, { useState, useEffect, useCallback } from 'react';
import { auth, db, functionsInstance } from '../../firebase/firebase'; // Adjust path if needed
import { httpsCallable } from 'firebase/functions';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { Bell, X } from 'lucide-react';

// ---------- UI for the notification pop-up ----------
const NotificationModal = ({ notification, onClose }) => {
  const createMarkup = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const textWithBreaks = text.replace(/\n/g, '<br />');
    const formattedText = textWithBreaks.replace(
      urlRegex,
      (url) =>
        `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-sky-600 underline hover:text-sky-800">${url}</a>`
    );
    return { __html: formattedText };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div
        className="relative bg-white p-8 rounded-2xl shadow-2xl max-w-lg w-full border-t-4 border-sky-500 transform transition-all duration-300 ease-out scale-95 opacity-0 animate-fade-in-scale"
        style={{ animationFillMode: 'forwards' }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors"
          aria-label="Close"
        >
          <X size={24} />
        </button>

        <div className="flex items-center">
          <div className="flex-shrink-0 bg-sky-100 p-3 rounded-full">
            <Bell className="h-6 w-6 text-sky-600" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-bold text-slate-900">A Quick Update</h3>
          </div>
        </div>

        <div
          className="mt-4 text-slate-600 prose prose-sm max-w-none whitespace-pre-wrap"
          dangerouslySetInnerHTML={createMarkup(notification.message)}
        />

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-2 bg-sky-600 text-white rounded-md font-semibold hover:bg-sky-700 transition-colors"
        >
          Got it, thanks!
        </button>
      </div>

      <style>{`
        @keyframes fade-in-scale {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fade-in-scale {
          animation-name: fade-in-scale;
          animation-duration: 0.3s;
        }
      `}</style>
    </div>
  );
};

// ---------- Main manager ----------
// UPDATED: Now accepts onVisibilityChange to notify parent
const NotificationManager = ({ onVisibilityChange }) => {
  const [notificationToShow, setNotificationToShow] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  // Helper to safely update parent state
  const notifyParent = useCallback((isVisible) => {
    if (onVisibilityChange) {
      onVisibilityChange(isVisible);
    }
  }, [onVisibilityChange]);

  const checkForNotifications = useCallback(async (user) => {
    if (!user) return;

    try {
      // 1) Fetch active notifications
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('isActive', '==', true)
      );
      const notifSnap = await getDocs(notificationsQuery);
      const activeNotifications = notifSnap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // 2) Fetch the user's dismissal states from subcollection
      const statesSnap = await getDocs(
        collection(db, 'users', user.uid, 'notificationStates')
      );
      const dismissedIds = new Set(
        statesSnap.docs
          .filter((d) => d.data()?.dismissed === true)
          .map((d) => d.id)
      );

      // 3) Filter by date window
      const now = new Date();
      const validNotifications = activeNotifications.filter((n) => {
        const startDate = n.startDate?.toDate ? n.startDate.toDate() : null;
        const endDateRaw = n.endDate?.toDate ? n.endDate.toDate() : null;
        const endDate =
          endDateRaw != null
            ? new Date(
                endDateRaw.getFullYear(),
                endDateRaw.getMonth(),
                endDateRaw.getDate(),
                23,
                59,
                59,
                999
              )
            : null;

        const afterStart = !startDate || now >= startDate;
        const beforeEnd = !endDate || now <= endDate;
        return afterStart && beforeEnd;
      });

      // 4) Find the first one the user hasn't dismissed
      const firstUnseen = validNotifications.find(
        (n) => !dismissedIds.has(n.id)
      );

      if (firstUnseen) {
        setNotificationToShow(firstUnseen);
        notifyParent(true); // Tell App.js "I am open!"

        // Increment view count via Cloud Function (best-effort)
        try {
          const incrementView = httpsCallable(
            functionsInstance,
            'incrementNotificationView'
          );
          await incrementView({ notificationId: firstUnseen.id });
        } catch (err) {
          console.error('Failed to increment notification view count:', err);
        }
      }
    } catch (error) {
      console.error('Error checking for notifications:', error);
    }
  }, [notifyParent]);

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setCurrentUser(user);
      if (user) {
        checkForNotifications(user);
      } else {
        setNotificationToShow(null);
        notifyParent(false);
      }
    });
    return () => unsubscribe();
  }, [checkForNotifications, notifyParent]);

  const handleCloseNotification = async () => {
    if (!currentUser || !notificationToShow) return;

    try {
      // ✅ Write to the per-user subcollection
      await setDoc(
        doc(
          db,
          'users',
          currentUser.uid,
          'notificationStates',
          notificationToShow.id
        ),
        {
          dismissed: true,
          dismissedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setNotificationToShow(null);
      notifyParent(false); // Tell App.js "I am closed, you can resume other popups"
    } catch (error) {
      console.error('Error dismissing notification:', error);
      setNotificationToShow(null);
      notifyParent(false);
    }
  };

  if (!notificationToShow) return null;

  return (
    <NotificationModal
      notification={notificationToShow}
      onClose={handleCloseNotification}
    />
  );
};

export default NotificationManager;